import { type ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Configs from '@/Configs';
import { useAuth } from '@/common/appContext';
import { Loader } from '@/common/support/loader';
import { KincodeButton } from '@/components/button/KincodeButton';
import GrowthLoopFooterCTA from '@/components/GrowthLoopFooterCTA';
import { UnifiedIcon } from '@/components/UnifiedIcon';
import { useToastMessage } from '@/utils/useToastMessage';
import { Pagination } from '@/widgets/pagination';

import { CompanyValueDescription } from '@/views/settings/company/CompanyValueDescription';

import {
  getPublicCareersPage,
  postPublicCareersApply,
} from './core/apiCalls';

const DEFAULT_PAGE_SIZE = 10;
const PAGE_SIZE_OPTIONS = [5, 10, 20, 50] as const;
const SEARCH_DEBOUNCE_MS = 300;

type CareersJob = {
  jobID: string;
  title: string;
  description?: string;
  updatedAt?: string | null;
};

type CareersValue = {
  name: string;
  description?: string;
};

type CareersPagination = {
  page: number;
  limit: number;
  total: number;
  pageCount: number;
  hasMore: boolean;
};

type CareersPage = {
  companyName?: string;
  brandColor?: string | null;
  logoUrl?: string | null;
  values?: CareersValue[];
  jobs?: CareersJob[];
  pagination?: CareersPagination;
  openJobsTotal?: number;
  q?: string | null;
};

function getPublicIdFromUrl(): string | null {
  try {
    const url = new URL(window.location.href);
    const parts = url.pathname.split('/').filter(Boolean);
    const idx = parts.findIndex((p) => p === 'careers');
    if (idx !== -1 && parts[idx + 1]) return parts[idx + 1];
    return parts[parts.length - 1] || null;
  } catch {
    return null;
  }
}

function emptyApplyForm() {
  return {
    firstName: '',
    lastName: '',
    email: '',
    linkedinUrl: '',
    note: '',
  };
}

/**
 * Public careers page — company brand, values, Open jobs (server search + pagination), apply flow.
 */
export function CareersPublic() {
  const { t } = useTranslation('careers');
  const { correlationId } = useAuth();
  const { errorToast } = useToastMessage();

  const publicId = useMemo(() => getPublicIdFromUrl(), []);
  const [isLoading, setIsLoading] = useState(true);
  const [isJobsRefreshing, setIsJobsRefreshing] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [data, setData] = useState<CareersPage | null>(null);
  const [selectedJob, setSelectedJob] = useState<CareersJob | null>(null);
  const [form, setForm] = useState(emptyApplyForm);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [jobSearch, setJobSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasLoadedOnceRef = useRef(false);

  const companyName = data?.companyName || String(t('careers.fallbackCompany'));
  const brandColor = data?.brandColor || Configs.companyColor || '#0f766e';
  const logoUrl = data?.logoUrl || null;
  const jobs = data?.jobs ?? [];
  const pagination = data?.pagination;
  const openJobsTotal = data?.openJobsTotal ?? pagination?.total ?? 0;
  const filteredTotal = pagination?.total ?? 0;
  const currentPage = pagination?.page ?? page;

  useEffect(() => {
    if (debounceRef.current != null) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      debounceRef.current = null;
      setDebouncedSearch(jobSearch.trim());
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (debounceRef.current != null) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [jobSearch]);

  useEffect(() => {
    let cancelled = false;
    async function fetchData() {
      if (!publicId) {
        setLoadError(true);
        setIsLoading(false);
        return;
      }
      const isFirstLoad = !hasLoadedOnceRef.current;
      if (isFirstLoad) {
        setIsLoading(true);
      } else {
        setIsJobsRefreshing(true);
      }
      setLoadError(false);
      try {
        const result = (await getPublicCareersPage(
          publicId,
          {
            q: debouncedSearch || undefined,
            page,
            limit: pageSize,
          },
          correlationId
        )) as CareersPage;
        if (!cancelled) {
          setData(result);
          hasLoadedOnceRef.current = true;
        }
      } catch {
        if (!cancelled) {
          setLoadError(true);
          if (!hasLoadedOnceRef.current) {
            setData(null);
          } else {
            errorToast(t('careers.refreshError'));
          }
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
          setIsJobsRefreshing(false);
        }
      }
    }
    void fetchData();
    return () => {
      cancelled = true;
    };
  }, [publicId, correlationId, debouncedSearch, page, pageSize, errorToast, t]);

  const handleSelectJob = useCallback((job: CareersJob) => {
    setSelectedJob(job);
    setSubmitted(false);
    setForm(emptyApplyForm());
  }, []);

  const handleCloseApply = useCallback(() => {
    setSelectedJob(null);
    setSubmitted(false);
    setForm(emptyApplyForm());
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!publicId || !selectedJob) return;
    if (!form.firstName.trim() || !form.lastName.trim() || !form.email.trim()) {
      errorToast(t('careers.apply.validation'));
      return;
    }
    setIsSubmitting(true);
    try {
      await postPublicCareersApply(
        {
          publicId,
          jobID: selectedJob.jobID,
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          email: form.email.trim(),
          linkedinUrl: form.linkedinUrl.trim() || undefined,
          note: form.note.trim() || undefined,
        },
        correlationId
      );
      setSubmitted(true);
    } catch {
      errorToast(t('careers.apply.error'));
    } finally {
      setIsSubmitting(false);
    }
  }, [publicId, selectedJob, form, correlationId, errorToast, t]);

  const scrollToJobs = useCallback(() => {
    document.getElementById('careers-open-roles')?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const handlePageChange = useCallback((nextPage: number) => {
    setPage(nextPage);
  }, []);

  const handleRowsPerPageChange = useCallback((event: ChangeEvent<HTMLSelectElement>) => {
    const nextSize = Number(event.target.value);
    if (!Number.isFinite(nextSize) || nextSize <= 0) {
      return;
    }
    setPageSize(nextSize);
    setPage(1);
  }, []);

  const jobsSubtitle = useMemo(() => {
    if (openJobsTotal === 0 && !debouncedSearch) {
      return t('careers.jobsEmpty');
    }
    if (debouncedSearch) {
      return t('careers.jobsSearchSubtitle', {
        shown: jobs.length,
        total: filteredTotal,
      });
    }
    return t('careers.jobsSubtitle', { count: openJobsTotal });
  }, [openJobsTotal, debouncedSearch, jobs.length, filteredTotal, t]);

  return (
    <div
      className="flex h-dvh flex-col overflow-y-auto bg-slate-50 dark:bg-slate-950"
      data-testid="careers-careers-public"
      style={{ ['--careers-brand' as string]: brandColor }}
    >
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-72 opacity-40 dark:opacity-25"
        style={{
          background: `linear-gradient(180deg, color-mix(in srgb, ${brandColor} 35%, transparent) 0%, transparent 100%)`,
        }}
        aria-hidden
      />

      <div className="relative mx-auto flex w-full max-w-5xl flex-1 flex-col px-4 py-8 sm:px-6 sm:py-10">
        <header
          className="mb-10 rounded-2xl border border-slate-200/80 bg-white/90 p-6 shadow-sm backdrop-blur dark:border-slate-700 dark:bg-slate-900/90 sm:p-8"
          data-testid="careers-careers-public-header"
        >
          <div className="flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex min-w-0 items-start gap-4">
              <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-2xl border border-slate-200 bg-white dark:border-slate-600 dark:bg-slate-800">
                {logoUrl ? (
                  <img
                    src={logoUrl}
                    alt={companyName}
                    className="h-10 w-10 object-contain"
                    loading="lazy"
                  />
                ) : (
                  <span
                    className="flex h-full w-full items-center justify-center text-lg font-semibold text-white"
                    style={{ backgroundColor: brandColor }}
                  >
                    {(companyName || 'C').slice(0, 1).toUpperCase()}
                  </span>
                )}
              </div>
              <div className="min-w-0">
                <p className="text-xs font-medium uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">
                  {t('careers.eyebrow')}
                </p>
                <h1 className="mt-1 truncate text-3xl font-semibold tracking-tight text-slate-900 dark:text-white sm:text-4xl">
                  {companyName}
                </h1>
                <p className="mt-2 max-w-xl text-base text-slate-600 dark:text-slate-300">
                  {t('careers.subtitle')}
                </p>
              </div>
            </div>

            {!isLoading && !loadError && openJobsTotal > 0 ? (
              <KincodeButton
                variant="primary"
                colorHex={brandColor}
                onClick={scrollToJobs}
                data-testid="careers-careers-public-view-roles"
              >
                {t('careers.viewRoles', { count: openJobsTotal })}
                <UnifiedIcon name="CaretDown" library="phosphor" className="h-4 w-4" />
              </KincodeButton>
            ) : null}
          </div>
        </header>

        {isLoading ? (
          <div className="space-y-4" data-testid="careers-careers-public-loading">
            <Loader />
            <div className="h-28 animate-pulse rounded-2xl bg-slate-200/80 dark:bg-slate-800" />
            <div className="h-24 animate-pulse rounded-2xl bg-slate-200/80 dark:bg-slate-800" />
            <div className="h-24 animate-pulse rounded-2xl bg-slate-200/80 dark:bg-slate-800" />
          </div>
        ) : loadError && !data ? (
          <div
            className="rounded-2xl border border-red-200 bg-red-50 p-6 text-red-800 dark:border-red-900 dark:bg-red-950/40 dark:text-red-200"
            data-testid="careers-careers-public-error"
          >
            <p className="font-medium">{t('careers.loadError')}</p>
            <p className="mt-1 text-sm opacity-90">{t('careers.loadErrorHint')}</p>
          </div>
        ) : (
          <>
            {(data?.values?.length ?? 0) > 0 ? (
              <section className="mb-12" data-testid="careers-careers-public-values">
                <h2 className="mb-1 text-xl font-semibold text-slate-900 dark:text-white">
                  {t('careers.valuesTitle')}
                </h2>
                <p className="mb-5 text-sm text-slate-600 dark:text-slate-400">
                  {t('careers.valuesSubtitle')}
                </p>
                <ul className="grid gap-3 sm:grid-cols-2">
                  {data?.values?.map((value) => (
                    <li
                      key={value.name}
                      className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900"
                    >
                      <div
                        className="mb-2 h-1 w-10 rounded-full"
                        style={{ backgroundColor: brandColor }}
                      />
                      <div className="font-medium text-slate-900 dark:text-white">{value.name}</div>
                      <CompanyValueDescription
                        description={value.description}
                        className="mt-1 text-sm leading-relaxed text-slate-600 dark:text-slate-400"
                        editorClassName="min-h-0 p-0 text-sm"
                        data-testid={`careers-value-description-${value.name}`}
                      />
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}

            <section id="careers-open-roles" data-testid="careers-careers-public-jobs">
              <div className="mb-5 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-slate-900 dark:text-white">
                    {t('careers.jobsTitle')}
                  </h2>
                  <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">{jobsSubtitle}</p>
                </div>
              </div>

              {openJobsTotal > 0 || debouncedSearch ? (
                <div className="relative mb-4" data-testid="careers-jobs-search">
                  <UnifiedIcon
                    name="MagnifyingGlass"
                    library="phosphor"
                    className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
                  />
                  <input
                    type="search"
                    value={jobSearch}
                    onChange={(e) => setJobSearch(e.target.value)}
                    placeholder={String(t('careers.jobsSearchPlaceholder'))}
                    aria-label={String(t('careers.jobsSearchPlaceholder'))}
                    className="w-full rounded-xl border border-slate-300 bg-white py-2.5 pl-10 pr-3 text-sm text-slate-900 outline-none ring-[var(--careers-brand)] placeholder:text-slate-400 focus:ring-2 dark:border-slate-600 dark:bg-slate-900 dark:text-white dark:placeholder:text-slate-500"
                    data-testid="careers-jobs-search-input"
                  />
                </div>
              ) : null}

              <div
                className={isJobsRefreshing ? 'opacity-60 transition-opacity' : undefined}
                data-testid="careers-jobs-list"
              >
                {openJobsTotal === 0 && !debouncedSearch ? null : jobs.length === 0 ? (
                  <p
                    className="rounded-2xl border border-dashed border-slate-300 bg-white/60 px-4 py-8 text-center text-sm text-slate-600 dark:border-slate-600 dark:bg-slate-900/40 dark:text-slate-300"
                    data-testid="careers-jobs-search-empty"
                  >
                    {t('careers.jobsSearchEmpty')}
                  </p>
                ) : (
                  <ul className="space-y-4">
                    {jobs.map((job) => (
                      <li
                        key={job.jobID}
                        className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-700 dark:bg-slate-900 sm:p-6"
                      >
                        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                          <div className="min-w-0 flex-1">
                            <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                              {job.title}
                            </h3>
                            {job.description ? (
                              <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-slate-600 dark:text-slate-300 line-clamp-4">
                                {job.description}
                              </p>
                            ) : (
                              <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
                                {t('careers.jobNoDescription')}
                              </p>
                            )}
                          </div>
                          <KincodeButton
                            variant="primary"
                            colorHex={brandColor}
                            onClick={() => handleSelectJob(job)}
                            className="shrink-0"
                            data-testid={`careers-job-${job.jobID}`}
                          >
                            {t('careers.applyCta')}
                            <UnifiedIcon name="ArrowRight" library="phosphor" className="h-4 w-4" />
                          </KincodeButton>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}

                {filteredTotal > 0 ? (
                  <div className="mt-4" data-testid="careers-jobs-pagination">
                    <Pagination
                      totalRows={filteredTotal}
                      rowsPerPage={pageSize}
                      currentPage={currentPage}
                      onPageChange={handlePageChange}
                      handleRowsPerPageChange={handleRowsPerPageChange}
                      pageSizeOptions={PAGE_SIZE_OPTIONS}
                      alwaysShowFooter
                    />
                  </div>
                ) : null}
              </div>
            </section>
          </>
        )}
      </div>

      <GrowthLoopFooterCTA />

      {selectedJob ? (
        <div
          className="fixed inset-0 z-[100] flex items-end justify-center bg-slate-950/50 p-0 backdrop-blur-[2px] sm:items-center sm:p-4"
          data-testid="careers-careers-public-apply"
          role="dialog"
          aria-modal="true"
          aria-labelledby="careers-apply-title"
        >
          <div className="flex max-h-[92vh] w-full max-w-lg flex-col overflow-hidden rounded-t-2xl bg-white shadow-2xl dark:bg-slate-900 sm:rounded-2xl">
            <div className="flex items-start justify-between gap-3 border-b border-slate-200 px-5 py-4 dark:border-slate-700">
              <div className="min-w-0">
                <p className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  {companyName}
                </p>
                <h3
                  id="careers-apply-title"
                  className="mt-0.5 text-lg font-semibold text-slate-900 dark:text-white"
                >
                  {submitted ? t('careers.apply.thanksTitle') : t('careers.apply.title')}
                </h3>
                <p className="truncate text-sm text-slate-600 dark:text-slate-300">
                  {selectedJob.title}
                </p>
              </div>
              <button
                type="button"
                onClick={handleCloseApply}
                className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-800 dark:hover:bg-slate-800 dark:hover:text-white"
                aria-label={String(t('careers.apply.close'))}
                data-testid="careers-apply-close"
              >
                <UnifiedIcon name="X" library="phosphor" className="h-5 w-5" />
              </button>
            </div>

            <div className="overflow-y-auto px-5 py-5">
              {submitted ? (
                <div className="py-6 text-center" data-testid="careers-apply-success">
                  <div
                    className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full text-white"
                    style={{ backgroundColor: brandColor }}
                  >
                    <UnifiedIcon name="Check" library="phosphor" className="h-7 w-7" />
                  </div>
                  <p className="text-base font-medium text-slate-900 dark:text-white">
                    {t('careers.apply.thanks')}
                  </p>
                  <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
                    {t('careers.apply.thanksHint')}
                  </p>
                  <KincodeButton
                    variant="primary"
                    colorHex={brandColor}
                    onClick={handleCloseApply}
                    className="mt-6"
                    data-testid="careers-apply-done"
                  >
                    {t('careers.apply.done')}
                  </KincodeButton>
                </div>
              ) : (
                <form
                  className="space-y-4"
                  onSubmit={(e) => {
                    e.preventDefault();
                    void handleSubmit();
                  }}
                >
                  <div className="grid gap-4 sm:grid-cols-2">
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-200">
                      {t('careers.apply.firstName')}
                      <input
                        className="mt-1.5 w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-slate-900 outline-none ring-[var(--careers-brand)] focus:ring-2 dark:border-slate-600 dark:bg-slate-950 dark:text-white"
                        value={form.firstName}
                        onChange={(e) => setForm((prev) => ({ ...prev, firstName: e.target.value }))}
                        required
                        autoComplete="given-name"
                        data-testid="careers-apply-first-name"
                      />
                    </label>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-200">
                      {t('careers.apply.lastName')}
                      <input
                        className="mt-1.5 w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-slate-900 outline-none ring-[var(--careers-brand)] focus:ring-2 dark:border-slate-600 dark:bg-slate-950 dark:text-white"
                        value={form.lastName}
                        onChange={(e) => setForm((prev) => ({ ...prev, lastName: e.target.value }))}
                        required
                        autoComplete="family-name"
                        data-testid="careers-apply-last-name"
                      />
                    </label>
                  </div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-200">
                    {t('careers.apply.email')}
                    <input
                      type="email"
                      className="mt-1.5 w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-slate-900 outline-none ring-[var(--careers-brand)] focus:ring-2 dark:border-slate-600 dark:bg-slate-950 dark:text-white"
                      value={form.email}
                      onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))}
                      required
                      autoComplete="email"
                      data-testid="careers-apply-email"
                    />
                  </label>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-200">
                    {t('careers.apply.linkedin')}
                    <input
                      className="mt-1.5 w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-slate-900 outline-none ring-[var(--careers-brand)] focus:ring-2 dark:border-slate-600 dark:bg-slate-950 dark:text-white"
                      value={form.linkedinUrl}
                      onChange={(e) =>
                        setForm((prev) => ({ ...prev, linkedinUrl: e.target.value }))
                      }
                      autoComplete="url"
                      placeholder="https://linkedin.com/in/…"
                      data-testid="careers-apply-linkedin"
                    />
                  </label>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-200">
                    {t('careers.apply.note')}
                    <textarea
                      className="mt-1.5 w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-slate-900 outline-none ring-[var(--careers-brand)] focus:ring-2 dark:border-slate-600 dark:bg-slate-950 dark:text-white"
                      rows={4}
                      value={form.note}
                      onChange={(e) => setForm((prev) => ({ ...prev, note: e.target.value }))}
                      placeholder={String(t('careers.apply.notePlaceholder'))}
                      data-testid="careers-apply-note"
                    />
                  </label>
                  <KincodeButton
                    type="submit"
                    variant="primary"
                    colorHex={brandColor}
                    disabled={isSubmitting}
                    className="w-full"
                    data-testid="careers-apply-submit"
                  >
                    {isSubmitting ? t('careers.apply.submitting') : t('careers.apply.submit')}
                  </KincodeButton>
                </form>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
